import { FlexBox, Spinner, Text } from '@dixa/lib-shared-habla';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { integrationsApi } from 'apis/integrations';
import { useServerStorage } from 'utils/useServerStorage';

import type { SidebarCardId } from '../../conversationSidebar.types';
import { useCardVisibility } from '../../hooks/useCardVisibility';
import { useSidebarMode } from '../../hooks/useSidebarMode';
import { reorderNumeric } from '../../utils/reorder';
import { CustomCardsItem } from '../customCardsItem';

export type CustomCardsProps = {
  isMinimized?: boolean;
  csid?: number | null;
};

const type = Symbol('customIntegrationCard');

export const CustomCards = ({
  isMinimized = false,
  csid,
}: CustomCardsProps): JSX.Element => {
  const [hiddenCardsSnapshot, setHiddenCardsSnapshot] = useState(
    new Set<SidebarCardId>(),
  );
  const { hiddenCards } = useCardVisibility();
  const sidebarMode = useSidebarMode();
  const { customCards, isFetching, isError } =
    integrationsApi.useReadCustomCardIntegrationsSanitizedQuery(undefined, {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        customCards: data?.data.custom_cards || [],
      }),
    });

  const storage = useServerStorage('user');

  const preparedCards = useMemo(() => {
    const cardOrder =
      storage.data.conversation?.sidebarCustomIntegrationOrder ?? {};

    return customCards
      .filter(
        (card) => sidebarMode === 'visibility' || !hiddenCards.has(card.id),
      )
      .sort(
        (a, b) =>
          (sidebarMode === 'visibility'
            ? Number(hiddenCardsSnapshot.has(b.id)) -
              Number(hiddenCardsSnapshot.has(a.id))
            : 0) ||
          (cardOrder[a.id] ?? -Infinity) - (cardOrder[b.id] ?? -Infinity) ||
          a.title.localeCompare(b.title),
      );
  }, [
    customCards,
    hiddenCards,
    hiddenCardsSnapshot,
    sidebarMode,
    storage.data.conversation?.sidebarCustomIntegrationOrder,
  ]);

  const handleMove = useCallback(
    ({ dragIndex, hoverIndex }: { dragIndex: number; hoverIndex: number }) => {
      storage.set({
        conversation: (draft) => {
          draft.sidebarCustomIntegrationOrder = reorderNumeric(
            preparedCards,
            dragIndex,
            hoverIndex,
          );
        },
      });
    },
    [storage, preparedCards],
  );

  useEffect(() => {
    if (sidebarMode === 'visibility') {
      setHiddenCardsSnapshot(hiddenCards);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarMode]);

  if (isError) {
    return (
      <FlexBox justifyContent="center" padding="400">
        <Text.Paragraph>
          Something went wrong fetching custom cards.
        </Text.Paragraph>
      </FlexBox>
    );
  }

  return (
    <>
      {isFetching ? (
        <FlexBox justifyContent="center" padding="400">
          <Spinner />
        </FlexBox>
      ) : (
        preparedCards.map((card, idx) => (
          <CustomCardsItem
            key={card.id}
            card={card}
            isMinimized={isMinimized}
            csid={csid}
            index={idx}
            type={type}
            onMove={handleMove}
          />
        ))
      )}
    </>
  );
};
